/**
 * Tools Landing Page
 * Entry point for the Tools section of Biodrone Portal
 */

import { Link } from 'react-router';
import styled from 'styled-components';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { LuPlane, LuClipboardList, LuMapPin, LuCloudSun } from 'react-icons/lu';

const PageContainer = styled(Container)`
	padding-top: 2rem;
	padding-bottom: 2rem;
`;

const PageTitle = styled.h1`
	color: ${props => props.theme.colors.textColor};
	margin-bottom: 0.5rem;
`;

const PageDescription = styled.p`
	color: ${props => props.theme.colors.textMuted};
	margin-bottom: 2rem;
`;

const ToolCard = styled(Card)`
	background: ${props => props.theme.colors.glass} !important;
	backdrop-filter: blur(16px);
	border: 1px solid ${props => props.theme.colors.glassBorder} !important;
	border-radius: 14px;
	transition: all 0.25s ease;
	height: 100%;

	&:hover {
		transform: translateY(-4px);
		box-shadow: 0 8px 32px rgba(52, 211, 153, 0.1);
		border-color: rgba(52, 211, 153, 0.25) !important;
	}
`;

const ToolCardBody = styled(Card.Body)`
	display: flex;
	flex-direction: column;
`;

const ToolIcon = styled.div`
	font-size: 2.5rem;
	margin-bottom: 1rem;
	color: ${props => props.theme.colors.primary || 'rgba(52, 211, 153, 0.85)'};
	display: flex;
	align-items: center;
`;

const ToolTitle = styled(Card.Title)`
	color: ${props => props.theme.colors.textColor};
	font-size: 1.25rem;
	margin-bottom: 0.75rem;
`;

const ToolDescription = styled(Card.Text)`
	color: ${props => props.theme.colors.textMuted};
	flex-grow: 1;
`;

const ToolLink = styled(Link)`
	text-decoration: none;
	color: inherit;

	&:hover {
		color: inherit;
	}
`;

const ComingSoonBadge = styled.span`
	background-color: ${props => props.theme.colors.warning || '#ffc107'};
	color: #000;
	padding: 0.25rem 0.5rem;
	border-radius: 4px;
	font-size: 0.75rem;
	font-weight: 600;
	margin-left: 0.5rem;
`;

const tools = [
	{
		id: 'flight-planner',
		title: 'Forestry Flight',
		description:
			'Plan drone flights for forestry operations. Draw flight areas on the map, configure flight parameters, and export to various drone platforms.',
		icon: LuPlane,
		path: '/tools/flight-planner',
		available: true,
	},
	{
		id: 'flight-templates',
		title: 'Flight Templates',
		description:
			'Manage reusable flight templates for your organization. Create, edit, and share templates with your team.',
		icon: LuClipboardList,
		path: '/admin/flight-templates',
		available: true,
	},
	{
		id: 'route-optimizer',
		title: 'Route Optimizer',
		description:
			'Optimize flight routes for maximum coverage with minimal flight time and battery consumption.',
		icon: LuMapPin,
		path: '/tools/route-optimizer',
		available: false,
	},
	{
		id: 'weather-check',
		title: 'Weather Check',
		description:
			'Check weather conditions and get recommendations for optimal flight times based on wind and precipitation.',
		icon: LuCloudSun,
		path: '/tools/weather',
		available: false,
	},
];

const Tools = () => {
	return (
		<PageContainer>
			<PageTitle>Tools</PageTitle>
			<PageDescription>
				Professional tools for drone operations and data analysis
			</PageDescription>

			<Row xs={1} md={2} lg={3} className="g-4">
				{tools.map(tool => {
					const IconComponent = tool.icon;
					return (
						<Col key={tool.id}>
							{tool.available ? (
								<ToolLink to={tool.path}>
									<ToolCard>
										<ToolCardBody>
											<ToolIcon>
												<IconComponent size={40} />
											</ToolIcon>
											<ToolTitle>{tool.title}</ToolTitle>
											<ToolDescription>{tool.description}</ToolDescription>
										</ToolCardBody>
									</ToolCard>
								</ToolLink>
							) : (
								<ToolCard style={{ opacity: 0.6, cursor: 'not-allowed' }}>
									<ToolCardBody>
										<ToolIcon>
											<IconComponent size={40} />
										</ToolIcon>
										<ToolTitle>
											{tool.title}
											<ComingSoonBadge>Coming Soon</ComingSoonBadge>
										</ToolTitle>
										<ToolDescription>{tool.description}</ToolDescription>
									</ToolCardBody>
								</ToolCard>
							)}
						</Col>
					);
				})}
			</Row>
		</PageContainer>
	);
};

export default Tools;
